import { Navigate, useParams } from 'react-router-dom';
import { QuickCheckoutLayout } from '../../../quickcheckout/common/QuickCheckoutLayout.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { css } from '@emotion/css';
import { getBridge } from '../../../Native.ts';
import { LeftColumn, StaticPage } from './StaticPage.tsx';
import {
    RequestStatus,
    useServerFetch,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { Spinner } from 'dg-web-shared/ui/Spinner';

type RecodingQrResponse = {
    status: RecodingQrStatus;
};

enum RecodingQrStatus {
    VALID = 'VALID',
    USED = 'USED',
    EXPIRED = 'EXPIRED',
}

export function RecodingQrLandingPage() {
    const params = useParams();
    const recodingQrId = params.cloudConnectorRecodingQrId ?? null;

    const bridge = getBridge();

    if (!recodingQrId) {
        return <Navigate to={'/'} />;
    }

    if (bridge) {
        return (
            <StaticPage>
                <LeftColumn>
                    <RecodingQrLandingPageText recodingQrId={recodingQrId} />
                </LeftColumn>
            </StaticPage>
        );
    }

    return (
        <QuickCheckoutLayout>
            <RecodingQrLandingPageText recodingQrId={recodingQrId} />
        </QuickCheckoutLayout>
    );
}

function RecodingQrLandingPageText({ recodingQrId }: { recodingQrId: string }) {
    return (
        <div
            className={css({
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: 22,
                textAlign: 'center',
                padding: '36px',
                lineHeight: 1.4,
            })}
        >
            <RecodingQrStatusDisplay recodingQrId={recodingQrId} />
        </div>
    );
}

function RecodingQrStatusDisplay({ recodingQrId }: { recodingQrId: string }) {
    const [recodingQr] = useServerFetch<
        RecodingQrResponse,
        { recodingQrId: string }
    >(
        ({ recodingQrId }) => ({
            url: `/ui-api/recoding-qr/${recodingQrId}`,
        }),
        { recodingQrId: recodingQrId },
    );

    switch (recodingQr.status) {
        case RequestStatus.NEVER_EXECUTED:
        case RequestStatus.PENDING:
            return <Spinner />;

        case RequestStatus.ERROR:
            return <RecodingQrError />;

        case RequestStatus.SUCCESS:
            switch (recodingQr.data.status) {
                case RecodingQrStatus.VALID:
                    return <RecodingQrValidText />;

                case RecodingQrStatus.USED:
                    return <RecodingQrUsedText />;

                case RecodingQrStatus.EXPIRED:
                    return <RecodingQrExpiredText />;
            }
    }
}

function RecodingQrValidText() {
    return (
        <>
            <p>
                <Localized
                    de="Um den Rabatt zu erhalten, müssen Sie an der Kasse Ihr Ticket und anschliessend diesen Rabatt-QR-Code scannen."
                    fr="Pour bénéficier de la réduction, veuillez vous rendre à la caisse et scanner d'abord votre ticket, puis ce code QR de rabais."
                    it="Per beneficiare del ribasso, deve passare alla cassa e scansionare prima il ticket e successivamente questo codice QR ribasso."
                    en="To benefit from the discount, you must go to the pay station and scan your ticket first, followed by this discount QR code."
                />
            </p>
            <p>
                <Localized
                    de="Im Falle eines offenen Restbetrags kann dieser an der Kasse oder per Smartphone beglichen werden."
                    fr="Dans le cas d'un montant restant dû, celui-ci peut être réglé à la caisse ou via smartphone."
                    it="Nel caso di un importo aperto residuo, questo potrà essere pagato alla cassa stessa oppure tramite smartphone."
                    en="If there is an outstanding balance, it can be paid at the pay station or via smartphone."
                />
            </p>
        </>
    );
}

function RecodingQrUsedText() {
    return (
        <p>
            <Localized
                de="Dieser Rabatt-QR-Code wurde bereits verwendet und ist daher nicht mehr gültig."
                fr="Ce code QR de rabais a déjà été utilisé et n'est donc plus valable."
                it="Questo codice QR ribasso è già stato utilizzato e non è quindi più valido."
                en="This discount QR code has already been used and is therefore no longer valid."
            />
        </p>
    );
}

function RecodingQrExpiredText() {
    return (
        <p>
            <Localized
                de="Dieser Rabatt-QR-Code ist abgelaufen (oder wurde eventuell annulliert) und ist daher nicht mehr gültig."
                fr="Ce code QR de rabais a expiré (ou a peut-être été annulé) et n'est donc plus valable."
                it="Questo codice QR ribasso è scaduto (oppure evt. è stato annullato) e non è quindi più valido."
                en="This discount QR code has expired (or may have been revoked) and is therefore no longer valid."
            />
        </p>
    );
}

function RecodingQrError() {
    return (
        <p>
            <Localized
                de="Rabatt-QR-Code konnte nicht geladen werden. Bitte versuchen Sie es erneut."
                fr="Le code QR de rabais n'a pas pu être chargé. Veuillez réessayer."
                it="Non è stato possibile caricare il codice QR ribasso. La preghiamo di riprovare."
                en="Discount QR code could not be loaded. Please try again."
            />
        </p>
    );
}
